import React from 'react'
import { Modal } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import type { FinancialEntry, VirtualEntry, AnyEntry } from '@/modules/billing/entries/entriesTypes'

interface Props {
  isOpen: boolean
  entry: FinancialEntry | VirtualEntry | AnyEntry | null
  date: string
  submitting: boolean
  onClose: () => void
  onConfirm: () => void
  setDate: (v: string) => void
  isMobile?: boolean
}

export default function PostponeModal({ isOpen, entry, date, submitting, onClose, onConfirm, setDate, isMobile = false }: Props) {
  const { t, locale } = useTranslation()
  const te = (k: string, p?: Record<string, string>) => t(`billing.entries.${k}`, p)
  if (!entry) return null

  const formatDate = (d?: string) => {
    if (!d) return ''
    try { return new Date(d).toLocaleDateString(locale) } catch { return d }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !submitting && onClose()}
      title={te('postpone.title')}
      size="sm"
      mobileTopAligned={false}
      headerLeft={isMobile ? (
        <button
          onClick={() => !submitting && onClose()}
          disabled={submitting}
          className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100 transition-colors"
          aria-label={t('actions.cancel') || 'Cancelar'}
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      ) : undefined}
      headerRight={(
        <div className="flex items-center gap-3">
          <button
            onClick={onConfirm}
            disabled={submitting || !date}
            className="flex items-center gap-2 text-emerald-600 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-100 disabled:opacity-40 transition-colors"
            aria-label="Confirmar"
          >
            {submitting ? (
              <svg className="animate-spin w-6 h-6" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
          <button
            onClick={() => !submitting && onClose()}
            disabled={submitting}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
            aria-label={t('actions.cancel') || 'Fechar'}
          >
            {!isMobile && (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </button>
        </div>
      )}
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{((entry as FinancialEntry).description || '').replace(/parcelamento/ig, '').trim()}</p>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{te('postpone.dateLabel')}</label>
          <input
            type="date"
            lang={locale}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={submitting}
            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>

        {(entry as FinancialEntry).original_due_date && (entry as FinancialEntry).original_due_date !== (entry as FinancialEntry).due_date && (
          <p className="text-xs text-slate-500">{te('postpone.originalLabel')}: <span className="line-through">{formatDate((entry as FinancialEntry).original_due_date!)}</span></p>
        )}
      </div>
    </Modal>
  )
}
